import type { Machine } from '@bridgewerk/shared';

export interface LiveMetricsCardProps {
  readonly machine: Machine;
}

export function LiveMetricsCard({ machine }: LiveMetricsCardProps): JSX.Element {
  return (
    <section className="rounded-md border border-border bg-card p-5">
      <header className="mb-4 flex items-center justify-between">
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-text-dim">
          Live metrics
        </h2>
        <span className="font-mono text-[10px] text-accent">WERK·OS</span>
      </header>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <BigMetric
          label="Spindle"
          value={Math.round(machine.spindle_rpm).toLocaleString()}
          unit="rpm"
        />
        <BigMetric
          label="Feed"
          value={Math.round(machine.feed_rate).toLocaleString()}
          unit="mm/min"
        />
        <BigMetric
          label="OEE"
          value={machine.oee_percent.toFixed(0)}
          unit="%"
          color={oeeColor(machine.oee_percent)}
        />
        <BigMetric
          label="Cycle time"
          value={formatDuration(machine.cycle_time_seconds)}
          unit=""
        />
      </div>
      {machine.program_name && (
        <p className="mt-4 text-xs text-text-dim font-mono">
          Program: <span className="text-text">{machine.program_name}</span>
        </p>
      )}
    </section>
  );
}

function BigMetric({
  label,
  value,
  unit,
  color,
}: {
  readonly label: string;
  readonly value: string;
  readonly unit: string;
  readonly color?: string;
}): JSX.Element {
  return (
    <div>
      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-text-dim">
        {label}
      </div>
      <div className="mt-1 font-mono">
        <span className="text-2xl" style={{ color: color ?? 'var(--color-text)' }}>
          {value}
        </span>
        {unit && <span className="ml-1 text-xs text-text-dim">{unit}</span>}
      </div>
    </div>
  );
}

function oeeColor(pct: number): string {
  if (pct >= 70) return 'var(--color-green)';
  if (pct >= 50) return 'var(--color-yellow)';
  return 'var(--color-red)';
}

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m === 0) return `${s}s`;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}
